import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import type { IMenuItemProps } from '../../../Menu.types';
import { createEventListeners } from './utils';
import { useMenuContext } from '../../MenuContext';
import classes from './style/MenuItem.module.scss';
import {
  MENU_ITEM_ANIMATION_CONFIG,
  MenuParts,
  INITIAL_ANIMATION_STATE,
  ANIMATION_PACKAGE_CONFIG,
} from '../../constants';
import { MenuItemLabel } from './MenuItemLabel';
import { isEmptyObject } from '@wix/editor-elements-common-utils';
import { useAnimationState } from './useAnimationState';
import { useDropdown } from './useDropdown';
import { DropdownContext } from '../../DropdownContext';

export const MenuItem: React.FC<IMenuItemProps> = props => {
  const {
    item,
    currentItem,
    focusOnMenuItem,
    getAnimationPackage,
    partToPreviewStateMap,
    HorizontalDropdown,
    VerticalDropdown,
    submenuDirection,
    itemClassName,
    dropdownItemClassName,
    dropdownSubItemClassName,
    onItemClick,
    onItemMouseIn,
    onItemMouseOut,
    onItemDblClick,
    submenuToggleIdPrefix,
  } = props;
  const { children, forceOpen = false } = item;

  const { animationState, initEnterAnimation, initExitAnimation } =
    useAnimationState(MENU_ITEM_ANIMATION_CONFIG, INITIAL_ANIMATION_STATE);

  const hasSubItems = !!item.items?.length;
  const hasMegaMenuContainer = !!children;
  const hasBuilderContainer = !!item.isContainerItem;
  const hasContainer = hasMegaMenuContainer || hasBuilderContainer;
  const hasDropdownMenu = hasContainer || hasSubItems;
  const hasLink = !isEmptyObject(item.link);

  const submenuToggleId =
    submenuToggleIdPrefix && hasSubItems
      ? `${submenuToggleIdPrefix}-${item.id}-submenu-toggle`
      : undefined;
  const submenuCheckboxRef = useRef<HTMLInputElement>(null);

  // Capture pre-hydration submenu state during render, before React recreates the DOM
  const prehydrationOpenRef = useRef<boolean | null>(null);
  if (prehydrationOpenRef.current === null) {
    if (submenuToggleId && typeof document !== 'undefined') {
      const checkbox = document.getElementById(
        submenuToggleId,
      ) as HTMLInputElement | null;
      prehydrationOpenRef.current = !!checkbox?.checked;
    } else {
      prehydrationOpenRef.current = false;
    }
  }

  const chevronButtonRef = useRef<HTMLButtonElement>(null);

  const itemRef = useRef<HTMLLIElement>(null);

  const getMenuItemAnimationName = () => {
    const animationPackage = getAnimationPackage();
    const menuItemAnimationName =
      ANIMATION_PACKAGE_CONFIG[animationPackage].menuItemAnimationName;

    return menuItemAnimationName;
  };

  const {
    isOpen,
    dropdownAnimationState,
    showDropdown,
    showDropdownImmediate,
    hideDropdown,
    toggleDropdown,
    updateDropdownDomStyles,
  } = useDropdown({
    itemRef,
    forceOpen,
    getAnimationPackage,
  });

  const { isHamburgerMenuOpened } = useMenuContext();

  const { setDropdownDomStylesApplier } = useContext(DropdownContext);
  // Only applies in Builder (DropdownContext is not provided in Studio implementation)
  setDropdownDomStylesApplier(updateDropdownDomStyles);

  const eventListeners = createEventListeners({
    showDropdown,
    hideDropdown,
    itemRef,
    getMenuItemAnimationName,
    initEnterAnimation,
    initExitAnimation,
    isOpen,
    hasLink,
    dropdownAnimationState,
  });

  useEffect(() => {
    if (currentItem?.id === item.id && itemRef.current) {
      focusOnMenuItem?.(itemRef.current);
    }
  }, [currentItem, item, focusOnMenuItem]);

  // CSS pre-hydration sync: if user expanded submenu via checkbox before hydration, sync to React state.
  // Also syncs horizontal mega menu hover: if the item is :hover when React hydrates
  // (visible via CSS pre-hydration rules), open the dropdown so JS takes over seamlessly.
  // useLayoutEffect runs before paint and before parent useEffect removes data-prehydration.
  useLayoutEffect(() => {
    if (
      (submenuCheckboxRef.current?.checked || prehydrationOpenRef.current) &&
      !isOpen
    ) {
      showDropdownImmediate();
    }

    if (hasDropdownMenu && !isOpen && itemRef.current?.matches(':hover')) {
      showDropdownImmediate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close submenu dropdown when hamburger menu closes.
  // HamburgerMenuContent stays mounted when prehydration is enabled,
  // so MenuItem's isOpen state persists across hamburger open/close cycles.
  const prevHamburgerOpenRef = useRef(isHamburgerMenuOpened);
  useEffect(() => {
    if (prevHamburgerOpenRef.current && !isHamburgerMenuOpened && isOpen) {
      hideDropdown();
    }
    prevHamburgerOpenRef.current = isHamburgerMenuOpened;
  }, [isHamburgerMenuOpened, isOpen, hideDropdown]);

  const handleEscKeyDown = useCallback(() => {
    chevronButtonRef.current?.focus();
    hideDropdown();
  }, [hideDropdown, chevronButtonRef]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleEscKeyDown();
      }
    },
    [handleEscKeyDown],
  );

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    } else {
      window.removeEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  const isElementInView = (element: HTMLLIElement) => {
    const boundingRect = element.getBoundingClientRect();
    return boundingRect.top >= 0 && boundingRect.bottom <= window.innerHeight;
  };

  const scrollIntoViewIfNeeded = () => {
    const element = itemRef.current;

    if (element && !isElementInView(element)) {
      element.scrollIntoView();
    }
  };

  const sharedDropdownProps = {
    item,
    currentItem,
    dropdownAnimationState,
    onEscKeyDown: handleEscKeyDown,
    itemClassName: dropdownItemClassName,
    subItemClassName: dropdownSubItemClassName,
    onItemClick,
    onItemMouseIn,
    onItemMouseOut,
    onItemDblClick,
  };

  return (
    <li
      ref={itemRef}
      className={classes.listItem}
      data-part={MenuParts.MenuItem}
      data-animation-name={animationState.name}
      data-animation-state={animationState.phase}
      data-item-depth="0" // For scrolling, to know how much items on depth=0
      onFocus={scrollIntoViewIfNeeded}
    >
      {submenuToggleId && (
        <input
          type="checkbox"
          id={submenuToggleId}
          ref={submenuCheckboxRef}
          data-submenu-toggle=""
          aria-hidden="true"
          tabIndex={-1}
          autoComplete="off"
          className={classes.hiddenToggle}
        />
      )}
      <div className={classes.itemWrapper} {...eventListeners}>
        <MenuItemLabel
          {...props}
          className={itemClassName}
          hasDropdownMenu={hasDropdownMenu}
          hasSubItems={hasSubItems}
          hasBuilderContainer={hasBuilderContainer}
          chevronButtonRef={chevronButtonRef}
          toggleDropdown={toggleDropdown}
          isOpen={isOpen}
          dropdownAnimationState={dropdownAnimationState}
          submenuToggleId={submenuToggleId}
        />
        {hasDropdownMenu && (
          <HorizontalDropdown
            {...sharedDropdownProps}
            children={children}
            direction={submenuDirection}
          />
        )}
      </div>
      {hasSubItems && (
        <VerticalDropdown
          {...sharedDropdownProps}
          isOpen={isOpen}
          partToPreviewStateMap={partToPreviewStateMap}
          direction={submenuDirection}
        />
      )}
      <span className={classes.divider}></span>
    </li>
  );
};
